import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { connectionService, taskService } from '../services';
import { formatConnectionString, formatData, handleError, Role, Asset } from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
} from '../components';
import {
  useInterval,
  useSideDialog,
  useModalDialog,
  useGlobalState,
  useNotFound,
  useMessage,
  useQueryParams,
} from '../hooks';

export const Connection = () => {
  const { queryParams, resetParams } = useQueryParams();

  const [connection, setConnection] = useState([]);
  const [connectionDetails, setConnectionDetails] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { connection_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  const { notFound, setNotFound, NotFound } = useNotFound();

  // Update job steps continuously
  useInterval(() => {
    fetchTasks();
  }, 3000);

  useEffect(() => {
    fetchItems();
    fetchTasks();
  }, [connection_id]);

  const fetchItems = async () => {
    try {
      const result = await Promise.all([
        connectionService.getById(connection_id).catch(err => {
          throw err;
        }),
        connectionService.getFormValues().catch(err => {
          console.error(err);
        }),
      ]);

      const conn = result[0]?.connections;
      const formValues = result[1]?.formValues;

      if (!conn || conn.length < 1) {
        setNotFound(true);
      } else {
        const connection_tmp = conn[0];

        const connectorsWithUrl = ['databricks', 'snowflake'];

        let hostname = connection_tmp.connection_hostname
          ? {
              title: 'Hostname',
              data: connection_tmp.connection_hostname,
              isUrl: connectorsWithUrl.includes(connection_tmp.connector_code),
            }
          : {};
        let database = connection_tmp.connection_database
          ? { title: 'Database', data: connection_tmp.connection_database }
          : {};
        let port = connection_tmp.connection_port
          ? { title: 'Port', data: connection_tmp.connection_port }
          : {};
        let service = connection_tmp.connection_service
          ? { title: 'Service', data: connection_tmp.connection_service }
          : {};

        const formValue = formValues && formValues[connection_tmp.connector_code];
        if (formValue) {
          hostname = formValue.hostname.show
            ? {
                title: formValue.hostname.label,
                data: connection_tmp.connection_hostname || formValue.hostname.defaultValue,
                isUrl: connectorsWithUrl.includes(connection_tmp.connector_code),
              }
            : {};
          database = formValue.database.show
            ? {
                title: formValue.database.label,
                data: connection_tmp.connection_database || formValue.database.defaultValue,
              }
            : {};
          port = formValue.port.show
            ? {
                title: formValue.port.label,
                data:
                  connection_tmp.connection_port ||
                  formValue.port.defaultValue ||
                  formValue.port.placeholder,
              }
            : {};
          service = formValue.service.show
            ? {
                title: formValue.service.label,
                data: connection_tmp.connection_service || formValue.service.defaultValue,
              }
            : {};
        }

        setConnectionDetails([hostname, port, service, database]);

        setConnection({
          ...connection_tmp,
          connector_name_tooltip:
            connection_tmp.connector_code === 'odbc'
              ? `${connection_tmp.connector_name} (${connection_tmp.connector_driver_name})`
              : connection_tmp.connector_name,
        });
      }
    } catch (err) {
      handleError({ err, showMessage, setNotFound });
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    const promise = new Promise((resolve, reject) => {
      taskService
        .getByConnectionId(connection_id)
        .then(res => {
          const tasks_tmp = res.tasks.map(task => ({
            ...task,
            task_is_active: task.job_status === 'running' || task.job_queue_position != null,
          }));
          setTasks(tasks_tmp);
          setSelectedItems(prev =>
            prev.map(sel => tasks_tmp.find(t => t.task_id === sel.task_id) ?? sel)
          );
          setLoadingTable(false);
          resolve(tasks_tmp);
        })
        .catch(err => {
          handleError({ err, showMessage }, () => {
            setLoadingTable(false);
          });
        });
    });

    Promise.all([promise]).catch(err => {
      showMessage(err, 'error');
    });
  };

  const updateJobStatus = (task_arr, setNewState, jobs) => {
    let tasks_tmp = task_arr;
    let i = -1;
    let tmp = null;

    jobs.forEach(job => {
      i = task_arr.findIndex(x => x.task_id === job.task_id);
      tmp = tasks_tmp[i];
      tasks_tmp[i] = {
        ...tmp,
        job_status: job.job_status,
        job_completion_message: job.job_completion_message,
      };
    });
    setNewState([...tasks_tmp]);
  };

  const addTask = async data => {
    const tasks = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .create(tasks)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchTasks();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editConnection = async (data, files) => {
    const connections = { connections: Array.isArray(data) ? data : [data] };

    await connectionService
      .update(connections, files)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editTask = async data => {
    const tasks = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .update(tasks)
      .then(async res => {
        fetchTasks();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const deleteConnection = async data => {
    await connectionService
      .destroy({ connections: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/connections`);
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Enqueues one or more tasks for execution via a single batch request.
   * Optimistically marks selected tasks as 'queued' in the UI before the request completes.
   * @param {Object[]} [data] - Explicit task list; falls back to selectedItems.
   */
  const runTask = async data => {
    const selectedTasks = data || selectedItems;

    updateJobStatus(
      tasks,
      setTasks,
      selectedTasks.map(task => ({
        task_id: task.task_id,
        job_status: 'queued',
        job_completion_message: 'Queued at ' + new Date().toLocaleString('sv-SE'),
      }))
    );
    setSelectedItems(prev =>
      prev.map(sel =>
        selectedTasks.find(t => t.task_id === sel.task_id) ? { ...sel, task_is_active: true } : sel
      )
    );

    await taskService
      .runMany(selectedTasks.map(t => t.task_id))
      .then(res => {
        res && showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        fetchTasks();
      });
  };

  /**
   * Aborts one or more tasks via a single batch request.
   * Queued tasks are dequeued immediately; running tasks receive an abort signal asynchronously.
   * @param {Object[]} [data] - Explicit task list; falls back to selectedItems.
   */
  const abortTask = async data => {
    const selectedTasks = data || selectedItems;

    await taskService
      .abortMany(selectedTasks.map(t => t.task_id))
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'))
      .finally(() => {
        fetchTasks();
      });
  };

  const deleteTask = async data => {
    await taskService
      .destroy({ tasks: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchTasks();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const data = loading ? [] : connection;

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const taskIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Connection"
          title={data.connection_name}
          subtitleIcon={<Icon name={data.connector_code} tooltip={data.connector_name_tooltip} />}
          subtitleText={data.connector_name}
          description={data.connection_description}
          loading={loading}
          details={[
            ...connectionDetails,
            { title: 'Driver', data: data.connector_driver_name },
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            { title: 'Created', data: formatData(data.created_timestamp, 'datetime') },
            {
              title: 'Id',
              data: data.connection_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editConnection', [data], editConnection),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteConnection', [data], deleteConnection),
            },
          ]}
        >
          <h3>{!loading ? 'System' : <SkeletonLoader width="5vw" />}</h3>
          <div className="display-linebreak">
            {!loading ? (
              data.system_name === null ? (
                '-'
              ) : (
                <Link to={'/systems/' + data.system_id} className="link">
                  {data.system_name}
                </Link>
              )
            ) : (
              <SkeletonLoader width="10vw" />
            )}
          </div>
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
        >
          <div
            label="Tasks"
            tabid="tasks"
            resultCount={loadingTable || tasks.length === 0 ? undefined : tasks.length}
          >
            <DataTable
              columns={[
                { id: 'job_status', name: '', type: 'icon', tooltip: 'job_completion_message' },
                {
                  id: 'task_name',
                  name: 'Name',
                  link: '/tasks/:task_id',
                  tooltip: 'task_description',
                  className: 'bold',
                },
                { id: 'job_completed_timestamp', name: 'Last Run Completed', type: 'datetime' },
                {
                  id: 'job_runtime_seconds',
                  name: 'Runtime [s]',
                  type: 'chart',
                  chartType: 'bar',
                  data: { data: 'job_runtime_seconds', max: 'job_runtime_seconds_max_value' },
                  tooltip: 'job_runtime_tooltip',
                },
                {
                  id: 'last_success_completed_timestamp',
                  name: 'Last Successful Run',
                  type: 'datetime',
                },
                {
                  id: 'task_is_enabled',
                  name: 'Status',
                  type: 'boolean',
                  values: taskIsEnabledValues,
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  onClick: items => showSideDialog('addTask', items || selectedItems, addTask),
                },
                {
                  label: 'Run',
                  action: 'run',
                  onClick: runTask,
                  disabled: [
                    {
                      field: 'job_status',
                      value: 'running',
                      tooltip: 'One or more selected tasks are already running',
                    },
                    {
                      field: 'task_is_enabled',
                      value: false,
                      tooltip: 'All selected tasks are disabled',
                    },
                  ],
                },
                {
                  label: 'Cancel',
                  action: 'abort',
                  onClick: abortTask,
                  hidden: { field: 'task_is_active', value: false },
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items => showSideDialog('editTask', items || selectedItems, editTask),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  onClick: items =>
                    showModalDialog('deleteTask', items || selectedItems, deleteTask),
                },
              ]}
              data={tasks}
              idColumn="task_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editable}
              filterable={true}
              filterObjects={['task_name', 'task_description', 'task_is_enabled']}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
